/**
 * The foot of a phone workshop list: how far ahead the list reaches, and the
 * button that reaches further.
 *
 * The caption is a live region that is on the page from the start, so a screen
 * reader hears it change as more loads, comes back empty, or fails. The button
 * stays focusable while loading, so keyboard focus is not dropped mid-press.
 */
import type { ReactElement } from 'react';

import { formatBlockDay, type DayRange } from './workshop-blocks';

export interface WorkshopsAheadFooterProps {
  /** The last day loaded so far, `YYYY-MM-DD`; null leaves it unnamed. */
  through: string | null;
  emptyBlock: DayRange | null;
  isLoadingMore: boolean;
  loadMoreFailed: boolean;
  onLoadMore: () => void;
}

function captionFor({
  through,
  emptyBlock,
  isLoadingMore,
  loadMoreFailed,
}: WorkshopsAheadFooterProps): string {
  if (isLoadingMore) return 'Loading more workshops…';
  if (loadMoreFailed) {
    const showing = through ? ` Showing through ${formatBlockDay(through)}.` : '';
    return `Couldn’t load more workshops.${showing}`;
  }
  if (emptyBlock) {
    return `No workshops ${formatBlockDay(emptyBlock.from)} – ${formatBlockDay(emptyBlock.to)}`;
  }
  return through ? `Through ${formatBlockDay(through)}` : '';
}

function buttonLabel({ isLoadingMore, loadMoreFailed }: WorkshopsAheadFooterProps): string {
  if (isLoadingMore) return 'Loading…';
  return loadMoreFailed ? 'Try again' : 'Load more workshops';
}

export function WorkshopsAheadFooter(props: WorkshopsAheadFooterProps): ReactElement {
  const failed = props.loadMoreFailed && !props.isLoadingMore;
  return (
    <div style={{ marginTop: 16, textAlign: 'center' }}>
      <p
        role="status"
        style={{
          margin: 0,
          fontSize: 12,
          color: failed ? 'var(--nb-risk)' : 'var(--nb-subtle)',
        }}
      >
        {captionFor(props)}
      </p>
      <button
        type="button"
        className="nb-btn"
        style={{ marginTop: 10, width: '100%', minHeight: 44 }}
        aria-disabled={props.isLoadingMore}
        onClick={() => {
          if (!props.isLoadingMore) props.onLoadMore();
        }}
      >
        {buttonLabel(props)}
      </button>
    </div>
  );
}
